// Check because this element is typically used in multiple places on the same page
if (!customElements.get('slider-component')) {
  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.slider');
      this.slides = Array.from(this.querySelectorAll('.slider__slide'));
      this.slideImages = Array.from(this.querySelectorAll('.multicolumn-slides__image'));
      this.prevButton = this.querySelector('.slider__button--prev');
      this.nextButton = this.querySelector('.slider__button--next');
      this.slidesPerView = parseInt(this.dataset.slidesPerView) || 3;

      this.currentIndex = 0;
      this.slideCount = this.slides.length;
      this.sliderInitialized = false;

      // Lightbox properties
      this.lightbox = null;
      this.lightboxMainImage = null;
      this.lightboxThumbnails = null;
      this.currentLightboxIndex = 0;

      // Bind methods for event listeners
      this.boundPrevSlide = this.prevSlide.bind(this);
      this.boundNextSlide = this.nextSlide.bind(this);

      // Initialize after connection to DOM
      if (this.isConnected) {
        this.init();
      }
    }

    connectedCallback() {
      if (!this.initialized) {
        this.init();
      }
    }

    init() {
      // Prevent multiple initializations
      if (this.initialized) return;
      this.initialized = true;

      // Add responsive handling
      window.addEventListener('resize', this.handleResize.bind(this));

      // Check if slider should be active based on screen size
      this.checkSliderState();

      // Initialize lightbox (always available)
      this.initLightbox();

      // Set initial state for desktop (hide buttons by default)
      this.initDesktopState();
    }

    checkSliderState() {
      const shouldUseSlider = this.shouldUseSlider();

      if (shouldUseSlider && !this.sliderInitialized) {
        // Initialize slider functionality
        this.initSlider();
        this.sliderInitialized = true;
      } else if (!shouldUseSlider && this.sliderInitialized) {
        // Destroy slider functionality
        this.destroySlider();
        this.sliderInitialized = false;
      }
    }

    shouldUseSlider() {
      // Slider should be active on screens smaller than 768px
      return window.innerWidth < 768;
    }

    initDesktopState() {
      // Hide navigation buttons initially on desktop
      if (this.prevButton) {
        this.prevButton.style.display = 'none';
      }
      if (this.nextButton) {
        this.nextButton.style.display = 'none';
      }
    }

    initSlider() {
      // Calculate visible slides based on screen width
      this.calculateSlidesPerView();

      // Apply initial styles to slides
      this.styleSlides();

      // Show navigation buttons
      if (this.prevButton) {
        this.prevButton.style.display = 'flex';
      }
      if (this.nextButton) {
        this.nextButton.style.display = 'flex';
      }

      // Add event listeners for navigation
      if (this.prevButton && this.nextButton) {
        this.prevButton.addEventListener('click', this.boundPrevSlide);
        this.nextButton.addEventListener('click', this.boundNextSlide);
      }

      // Show initial slides
      this.updateSlides();

      // Set button states
      this.updateButtonStates();
    }

    destroySlider() {
      // Remove event listeners
      if (this.prevButton && this.nextButton) {
        this.prevButton.removeEventListener('click', this.boundPrevSlide);
        this.nextButton.removeEventListener('click', this.boundNextSlide);
      }

      // Reset slide styles to their natural state
      this.slides.forEach(slide => {
        slide.style.flex = '';
        slide.style.maxWidth = '';
      });

      // Reset slider position
      const slidesContainer = this.querySelector('.slider__slides');
      if (slidesContainer) {
        slidesContainer.style.transform = '';
      }

      // Reset current index
      this.currentIndex = 0;

      // Hide navigation buttons on desktop
      if (this.prevButton) {
        this.prevButton.style.display = 'none';
      }
      if (this.nextButton) {
        this.nextButton.style.display = 'none';
      }
    }

    initLightbox() {
      // Create lightbox elements
      this.createLightbox();

      // Add click event to each image
      this.slideImages.forEach((image, index) => {
        image.addEventListener('click', () => {
          this.openLightbox(index);
        });
        // Make images look clickable
        image.style.cursor = 'pointer';
      });
    }

    createLightbox() {
      // Create lightbox container
      this.lightbox = document.createElement('div');
      this.lightbox.className = 'slider-lightbox';

      // Create main image container
      const mainImageContainer = document.createElement('div');
      mainImageContainer.className = 'slider-lightbox__main';

      // Create main image
      this.lightboxMainImage = document.createElement('img');
      mainImageContainer.appendChild(this.lightboxMainImage);

      // Create thumbnails container
      this.lightboxThumbnails = document.createElement('div');
      this.lightboxThumbnails.className = 'slider-lightbox__thumbnails';

      // Create close button
      const closeButton = document.createElement('button');
      closeButton.className = 'slider-lightbox__close';
      closeButton.innerHTML = '×';
      closeButton.addEventListener('click', this.closeLightbox.bind(this));

      // Create navigation buttons
      const prevButton = document.createElement('button');
      prevButton.className = 'slider-lightbox__nav slider-lightbox__prev';
      //create and  insert a span inside the button
      const prevSpan = document.createElement('span');
      prevSpan.className = 'slider-lightbox__nav-icon';
      prevSpan.innerHTML = '‹';
      prevButton.appendChild(prevSpan);
      prevButton.addEventListener('click', () => this.navigateLightbox(-1));

      const nextButton = document.createElement('button');
      nextButton.className = 'slider-lightbox__nav slider-lightbox__next';
      const nextSpan = document.createElement('span');
      nextSpan.className = 'slider-lightbox__nav-icon';
      nextSpan.innerHTML = '›';
      nextButton.appendChild(nextSpan);
      nextButton.addEventListener('click', () => this.navigateLightbox(1));

      // Assemble lightbox
      this.lightbox.appendChild(closeButton);
      this.lightbox.appendChild(prevButton);
      this.lightbox.appendChild(mainImageContainer);
      this.lightbox.appendChild(nextButton);
      this.lightbox.appendChild(this.lightboxThumbnails);

      // Add click event to close when clicking on the backdrop
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) {
          this.closeLightbox();
        }
      });

      // Add keyboard navigation
      document.addEventListener('keydown', this.handleLightboxKeyboard.bind(this));

      // Append to the component
      this.appendChild(this.lightbox);
    }

    openLightbox(index) {
      this.currentLightboxIndex = index;
      this.createLightboxThumbnails();
      this.updateLightboxImage();
      this.lightbox.classList.add('active');
      document.body.classList.add('lightbox-open');
    }

    closeLightbox() {
      this.lightbox.classList.remove('active');
      document.body.classList.remove('lightbox-open');
    }

    createLightboxThumbnails() {
      this.lightboxThumbnails.innerHTML = '';
      this.slideImages.forEach((image, index) => {
        const thumbnail = document.createElement('div');
        thumbnail.className = 'slider-lightbox__thumbnail';
        if (index === this.currentLightboxIndex) {
          thumbnail.classList.add('active');
        }

        const thumbnailImg = document.createElement('img');
        thumbnailImg.src = image.src;
        thumbnailImg.alt = image.alt || '';

        thumbnail.appendChild(thumbnailImg);
        thumbnail.addEventListener('click', () => {
          this.currentLightboxIndex = index;
          this.updateLightboxImage();
        });

        this.lightboxThumbnails.appendChild(thumbnail);
      });
    }

    updateLightboxImage() {
      const currentImage = this.slideImages[this.currentLightboxIndex];
      this.lightboxMainImage.src = currentImage.src;
      this.lightboxMainImage.alt = currentImage.alt || '';

      // Update active thumbnail
      const thumbnails = this.lightboxThumbnails.querySelectorAll('.slider-lightbox__thumbnail');
      thumbnails.forEach((thumb, index) => {
        if (index === this.currentLightboxIndex) {
          thumb.classList.add('active');
        } else {
          thumb.classList.remove('active');
        }
      });
    }

    navigateLightbox(direction) {
      const newIndex = this.currentLightboxIndex + direction;
      if (newIndex >= 0 && newIndex < this.slideImages.length) {
        this.currentLightboxIndex = newIndex;
        this.updateLightboxImage();
      }
    }

    handleLightboxKeyboard(e) {
      // Only handle events when lightbox is active
      if (!this.lightbox || !this.lightbox.classList.contains('active')) return;

      switch (e.key) {
        case 'ArrowLeft':
          this.navigateLightbox(-1);
          break;
        case 'ArrowRight':
          this.navigateLightbox(1);
          break;
        case 'Escape':
          this.closeLightbox();
          break;
      }
    }

    calculateSlidesPerView() {
      // Adjust slides per view based on screen width
      const windowWidth = window.innerWidth;
      if (windowWidth < 768) {
        this.slidesPerView = 1;
      } else if (windowWidth < 1024) {
        this.slidesPerView = Math.min(2, parseInt(this.dataset.slidesPerView) || 3);
      } else {
        this.slidesPerView = parseInt(this.dataset.slidesPerView) || 3;
      }
    }

    styleSlides() {
      // Set width for each slide based on slidesPerView
      this.slides.forEach(slide => {
        slide.style.flex = `0 0 ${100 / this.slidesPerView}%`;
        slide.style.maxWidth = `${100 / this.slidesPerView}%`;
      });
    }

    handleResize() {
      // Check if slider state should change based on new screen size
      this.checkSliderState();

      // Only update slider-specific properties if slider is active
      if (this.sliderInitialized) {
        const previousSlidesPerView = this.slidesPerView;
        this.calculateSlidesPerView();

        // Only update if slidesPerView changed
        if (previousSlidesPerView !== this.slidesPerView) {
          this.styleSlides();

          // Adjust currentIndex if needed
          if (this.currentIndex > this.slideCount - this.slidesPerView) {
            this.currentIndex = Math.max(0, this.slideCount - this.slidesPerView);
          }

          this.updateSlides();
          this.updateButtonStates();
        }
      }
    }

    updateSlides() {
      const offset = -this.currentIndex * (100 / this.slidesPerView);
      this.querySelector('.slider__slides').style.transform = `translateX(${offset}%)`;
    }

    updateButtonStates() {
      if (this.prevButton) {
        this.prevButton.disabled = this.currentIndex <= 0;
        this.prevButton.classList.toggle('disabled', this.currentIndex <= 0);
      }

      if (this.nextButton) {
        const lastVisibleSlide = this.currentIndex + this.slidesPerView;
        this.nextButton.disabled = lastVisibleSlide >= this.slideCount;
        this.nextButton.classList.toggle('disabled', lastVisibleSlide >= this.slideCount);
      }
    }

    prevSlide() {
      if (!this.sliderInitialized) return;
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateSlides();
        this.updateButtonStates();
      }
    }

    nextSlide() {
      if (!this.sliderInitialized) return;
      const lastVisibleSlide = this.currentIndex + this.slidesPerView;
      if (lastVisibleSlide < this.slideCount) {
        this.currentIndex++;
        this.updateSlides();
        this.updateButtonStates();
      }
    }
  }

  // Register the custom element
  customElements.define('slider-component', SliderComponent);
}


